import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle } from 'lucide-react'
import { getCreditNote, approveCreditNote } from '../services/api'
import { formatCurrency, formatDate, statusBadgeClass } from '../lib/formatters'

export default function CreditNoteDetail() {
  const { id } = useParams()
  const [cn, setCn] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)

  const load = () => {
    setLoading(true)
    getCreditNote(id).then((res) => setCn(res.data)).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [id])

  const handleApprove = async () => {
    if (!confirm('Approve this credit note? A number will be assigned.')) return
    setActionLoading(true)
    try { await approveCreditNote(id); load() }
    catch (err) { alert(err.response?.data?.error || 'Failed') }
    finally { setActionLoading(false) }
  }

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-8 w-48 bg-gray-200 rounded" /><div className="h-64 bg-gray-100 rounded" /></div>
  if (!cn) return null

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/credit-notes" className="p-1.5 text-gray-400 hover:text-gray-600"><ArrowLeft className="h-5 w-5" /></Link>
        <h1 className="text-2xl font-semibold text-gray-900">{cn.number || 'DRAFT'}</h1>
        <span className={`badge ${statusBadgeClass(cn.status)}`}>{cn.status}</span>
      </div>

      {cn.status === 'DRAFT' && (
        <div className="flex gap-2">
          <Link to={`/credit-notes/${id}/edit`} className="btn-secondary">Edit</Link>
          <button onClick={handleApprove} disabled={actionLoading} className="btn-primary flex items-center gap-1.5">
            <CheckCircle className="h-4 w-4" /> Approve
          </button>
        </div>
      )}

      <div className="card grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div><span className="text-gray-500 block">Customer</span><span className="font-medium">{cn.customer?.name || '—'}</span></div>
        <div><span className="text-gray-500 block">Date</span><span>{formatDate(cn.date)}</span></div>
        <div><span className="text-gray-500 block">Linked Invoice</span><span>{cn.linkedInvoice?.number || cn.linkedInvoiceId || '—'}</span></div>
        <div><span className="text-gray-500 block">Place of Supply</span><span>{cn.placeOfSupply || '—'}</span></div>
        <div><span className="text-gray-500 block">Total</span><span className="font-semibold">{formatCurrency(cn.total, cn.currency)}</span></div>
        {cn.reason && <div className="col-span-2"><span className="text-gray-500 block">Reason</span><span>{cn.reason}</span></div>}
      </div>

      <div className="card overflow-hidden">
        <h2 className="text-lg font-medium text-gray-900 mb-3">Line Items</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 bg-gray-50 border-b">
                <th className="px-3 py-2 font-medium">Description</th>
                <th className="px-3 py-2 font-medium">HSN/SAC</th>
                <th className="px-3 py-2 font-medium text-right">Qty</th>
                <th className="px-3 py-2 font-medium text-right">Rate</th>
                <th className="px-3 py-2 font-medium text-right">GST %</th>
                <th className="px-3 py-2 font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {cn.lineItems?.map((li, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="px-3 py-2">{li.description}</td>
                  <td className="px-3 py-2">{li.hsnSacCode || '—'}</td>
                  <td className="px-3 py-2 text-right">{li.qty}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(li.rate, cn.currency)}</td>
                  <td className="px-3 py-2 text-right">{Number(li.gstRate)}%</td>
                  <td className="px-3 py-2 text-right font-medium">{formatCurrency(li.total, cn.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-4">
          <div className="w-64 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(cn.subtotal, cn.currency)}</span></div>
            {Number(cn.cgst) > 0 && <div className="flex justify-between"><span className="text-gray-500">CGST</span><span>{formatCurrency(cn.cgst, cn.currency)}</span></div>}
            {Number(cn.sgst) > 0 && <div className="flex justify-between"><span className="text-gray-500">SGST</span><span>{formatCurrency(cn.sgst, cn.currency)}</span></div>}
            {Number(cn.igst) > 0 && <div className="flex justify-between"><span className="text-gray-500">IGST</span><span>{formatCurrency(cn.igst, cn.currency)}</span></div>}
            <div className="flex justify-between font-semibold border-t pt-2"><span>Total</span><span>{formatCurrency(cn.total, cn.currency)}</span></div>
          </div>
        </div>
      </div>

      {cn.notes && <div className="card"><h2 className="text-sm font-medium text-gray-500 mb-1">Notes</h2><p className="text-sm text-gray-700 whitespace-pre-wrap">{cn.notes}</p></div>}
    </div>
  )
}
